export const defaultAnimationProfile = Object.freeze({
  bodyLanguagePreset: 'neutral',
  basePosture: 'auto',
  gestureIntensity: 0.55,
  postureVariation: 0.15,
  listeningResponsiveness: 0.45,
  semanticReactionsEnabled: true,
});

export const bodyLanguagePresetOptions = Object.freeze([
  {
    value: 'neutral',
    label: 'Neutral',
    description: 'Natural, mostly upright movement suitable for most patients.',
  },
  {
    value: 'reserved',
    label: 'Reserved',
    description: 'Quieter gestures with more restrained, closed body language.',
  },
  {
    value: 'expressive',
    label: 'Expressive',
    description: 'More animated speaking gestures and engaged movement.',
  },
  {
    value: 'anxious',
    label: 'Anxious',
    description: 'More watchful, restless-looking attention and movement.',
  },
  {
    value: 'tired',
    label: 'Tired',
    description: 'Subdued movement that favours slouched or leaned-back sitting.',
  },
  {
    value: 'defensive',
    label: 'Defensive',
    description: 'Guarded movement that favours crossed arms and leaning back.',
  },
]);

const bodyLanguagePresetSettings = Object.freeze({
  neutral: Object.freeze({
    gestureIntensity: 0.55,
    postureVariation: 0.15,
    listeningResponsiveness: 0.45,
  }),
  reserved: Object.freeze({
    gestureIntensity: 0.25,
    postureVariation: 0.15,
    listeningResponsiveness: 0.35,
  }),
  expressive: Object.freeze({
    gestureIntensity: 0.8,
    postureVariation: 0.65,
    listeningResponsiveness: 0.7,
  }),
  anxious: Object.freeze({
    gestureIntensity: 0.55,
    postureVariation: 0.65,
    listeningResponsiveness: 0.75,
  }),
  tired: Object.freeze({
    gestureIntensity: 0.3,
    postureVariation: 0.15,
    listeningResponsiveness: 0.25,
  }),
  defensive: Object.freeze({
    gestureIntensity: 0.25,
    postureVariation: 0.15,
    listeningResponsiveness: 0.35,
  }),
});

export const getBodyLanguagePresetSettings = (preset) => (
  bodyLanguagePresetSettings[preset] || bodyLanguagePresetSettings.neutral
);

export const basePostureOptions = Object.freeze([
  { value: 'auto', label: 'Auto per session' },
  { value: 'normal', label: 'Normal' },
  { value: 'leanBack', label: 'Lean back' },
  { value: 'crossLegged', label: 'Cross legged' },
  { value: 'armsCrossed', label: 'Arms crossed' },
  { value: 'slouched', label: 'Slouched' },
]);

const clampUnit = (value, fallback) => {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return fallback;
  }

  return Math.max(0, Math.min(1, number));
};

const pickOption = (value, options, fallback) => (
  options.some((option) => option.value === value) ? value : fallback
);

export const normalizeAnimationProfile = (profile = {}) => {
  const source = profile && typeof profile === 'object' ? profile : {};

  return {
    bodyLanguagePreset: pickOption(
      source.bodyLanguagePreset,
      bodyLanguagePresetOptions,
      defaultAnimationProfile.bodyLanguagePreset
    ),
    basePosture: pickOption(
      source.basePosture,
      basePostureOptions,
      defaultAnimationProfile.basePosture
    ),
    gestureIntensity: clampUnit(
      source.gestureIntensity,
      defaultAnimationProfile.gestureIntensity
    ),
    postureVariation: clampUnit(
      source.postureVariation,
      defaultAnimationProfile.postureVariation
    ),
    listeningResponsiveness: clampUnit(
      source.listeningResponsiveness,
      defaultAnimationProfile.listeningResponsiveness
    ),
    semanticReactionsEnabled: source.semanticReactionsEnabled !== false,
  };
};
